import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner } from 'reactstrap';
import styled from '@emotion/styled';
import { Button } from '../../Components/Button';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { HttpBackend } from '../../Services/Http/HttpBackend';

interface PasswordPolicy {
  minLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumbers: boolean;
  requireSpecialCharacters: boolean;
  noMoreThanTwoIdenticalChars: boolean;
}

const Description = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const StyledInput = styled(Input)`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 15px;
  border: 1px solid ${Theme.Colors.lighterGrey};

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }

  &.is-invalid {
    border-color: #dc3545;
  }
`;

const RequirementsContainer = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 12px;
  padding: 16px;
  margin-top: 20px;
`;

const RequirementsTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 12px 0;
`;

const Requirement = styled.div<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  color: ${(props) => (props.$met ? Theme.Colors.success : Theme.Colors.charcoal)};

  i {
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 12px 16px;
  color: #dc2626;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: ${Theme.Colors.success}15;
  border: 1px solid ${Theme.Colors.success};
  border-radius: 8px;
  padding: 16px;
  color: ${Theme.Colors.success};
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
  color: ${Theme.Colors.charcoal};
`;

const HelperText = styled.small`
  color: #dc3545;
  display: block;
  margin-top: 4px;
`;

export type ChangePasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: 'change' | 'set';
};

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = observer(({ isOpen, onClose, mode }) => {
  const { t } = useTrans();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(true);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch password policy
  useEffect(() => {
    if (!isOpen) return;

    const fetchPolicy = async () => {
      setIsLoadingPolicy(true);
      try {
        const policy = await HttpBackend.get('/uaa/authentication/password-policy');
        setPasswordPolicy(policy);
      } catch (err) {
        console.error('Error fetching password policy:', err);
        // Fallback policy
        setPasswordPolicy({
          minLength: 8,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: true,
          noMoreThanTwoIdenticalChars: true,
        });
      } finally {
        setIsLoadingPolicy(false);
      }
    };

    fetchPolicy();
  }, [isOpen]);

  // Password validation
  const passwordChecks = useMemo(() => {
    if (!passwordPolicy) return [];

    return [
      {
        label: t('profile.password.reqMinLength', {
          defaultValue: `At least ${passwordPolicy.minLength} characters`,
          minLength: passwordPolicy.minLength,
        }),
        met: password.length >= passwordPolicy.minLength,
        required: true,
      },
      {
        label: t('profile.password.reqLowercase', { defaultValue: 'Lowercase letters (a-z)' }),
        met: /[a-z]/.test(password),
        required: passwordPolicy.requireLowercase,
      },
      {
        label: t('profile.password.reqUppercase', { defaultValue: 'Uppercase letters (A-Z)' }),
        met: /[A-Z]/.test(password),
        required: passwordPolicy.requireUppercase,
      },
      {
        label: t('profile.password.reqNumbers', { defaultValue: 'Numbers (0-9)' }),
        met: /[0-9]/.test(password),
        required: passwordPolicy.requireNumbers,
      },
      {
        label: t('profile.password.reqSpecial', { defaultValue: 'Special characters (!@#$%^&*)' }),
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        required: passwordPolicy.requireSpecialCharacters,
      },
    ].filter((check) => check.required);
  }, [password, passwordPolicy, t]);

  const isPasswordValid = passwordChecks.every((check) => check.met);
  const doPasswordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = isPasswordValid && doPasswordsMatch;

  const handleClose = useCallback(() => {
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    const endpoint = mode === 'set' ? '/uaa/profile/set-initial-password' : '/uaa/profile/change-password';

    try {
      await HttpBackend.post(endpoint, { newPassword: password });
      setSuccess(true);
    } catch (err: any) {
      console.error(`Error ${mode === 'set' ? 'setting' : 'changing'} password:`, err);
      setError(
        err?.errorMessage ||
        err?.message ||
        t('profile.password.changeRequestErrorMessage', {
          defaultValue: 'Failed to change password. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [password, canSubmit, mode, t]);

  const title = mode === 'set'
    ? t('profile.password.setPasswordTitle', { defaultValue: 'Set Password' })
    : t('profile.password.changePasswordTitle', { defaultValue: 'Change Password' });

  const successTitle = mode === 'set'
    ? t('profile.password.setSuccessTitle', { defaultValue: 'Password Set' })
    : t('profile.password.changeSuccessTitle', { defaultValue: 'Success' });

  const successMessage = mode === 'set'
    ? t('profile.password.setSuccessMessage', {
        defaultValue: 'Password set successfully! You can now log in using your email and new password.',
      })
    : t('profile.password.changeSuccessMessage', {
        defaultValue: 'Your password has been successfully changed.',
      });

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalHeader toggle={handleClose}>{title}</ModalHeader>
      <ModalBody>
        {isLoadingPolicy ? (
          <LoadingContainer>
            <Spinner color="primary" />
            <span>{t('profile.password.loadingPolicy', { defaultValue: 'Loading password requirements...' })}</span>
          </LoadingContainer>
        ) : success ? (
          <SuccessMessage>
            <i className="bi bi-check-circle" style={{ fontSize: 32, marginBottom: 12, display: 'block' }} />
            <strong style={{ display: 'block', marginBottom: 8 }}>{successTitle}</strong>
            {successMessage}
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            {mode === 'set' && (
              <Description>
                {t('profile.password.setInfo', {
                  defaultValue:
                    'You are currently using passwordless email login. Set a password below to enable email & password login.',
                })}
              </Description>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <FormGroup>
              <Label for="newPassword">
                {t('profile.password.newPasswordLabel', { defaultValue: 'New Password' })}
              </Label>
              <StyledInput
                type="password"
                id="newPassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                invalid={password.length > 0 && !isPasswordValid}
                autoComplete="new-password"
                autoFocus
              />
            </FormGroup>

            <FormGroup>
              <Label for="confirmPassword">
                {t('profile.password.confirmPasswordLabel', { defaultValue: 'Confirm Password' })}
              </Label>
              <StyledInput
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                invalid={confirmPassword.length > 0 && !doPasswordsMatch}
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && !doPasswordsMatch && (
                <HelperText>
                  {t('profile.password.errorMismatch', { defaultValue: 'Passwords do not match' })}
                </HelperText>
              )}
            </FormGroup>

            <RequirementsContainer>
              <RequirementsTitle>
                {t('profile.password.requirementsTitle', { defaultValue: 'Password Requirements:' })}
              </RequirementsTitle>
              {passwordChecks.map((check, index) => (
                <Requirement key={index} $met={check.met}>
                  <i className={`bi ${check.met ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                  {check.label}
                </Requirement>
              ))}
            </RequirementsContainer>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        {success ? (
          <Button color="primary" onClick={handleClose}>
            {t('globals.done', { defaultValue: 'Done' })}
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
              {t('globals.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={!canSubmit || isSubmitting || isLoadingPolicy}>
              {isSubmitting ? (
                <Spinner size="sm" />
              ) : mode === 'set' ? (
                t('profile.password.setPasswordButton', { defaultValue: 'Set Password' })
              ) : (
                t('profile.password.submitChangeButton', { defaultValue: 'Change Password' })
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
});
